import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApidaeTouristicObject } from '@alterrego/module-apidae';
import { EventCard } from '@alterrego/core-ui';
import './AgendaScreen.css';

const BACKEND_URL = 'https://api.apps.alterrego.com';
const COUNT = 20;

function formatDateRange(debut?: string, fin?: string): string {
  if (!debut) return '';
  const d = new Date(debut + 'T12:00:00');
  const start = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  if (!fin || fin === debut) return start;
  const e = new Date(fin + 'T12:00:00');
  const sameYear = d.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && d.getMonth() === e.getMonth();
  if (sameMonth) return `${d.getDate()} – ${e.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  if (sameYear) return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${e.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  return `${start} – ${e.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function getTag(evt: ApidaeTouristicObject): string | undefined {
  return evt.informationsFeteEtManifestation?.categories?.[0]?.libelleFr
    ?? evt.informationsFeteEtManifestation?.themes?.[0]?.libelleFr
    ?? undefined;
}

export function AgendaScreen() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ApidaeTouristicObject[]>([]);
  const [numFound, setNumFound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [first, setFirst] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${BACKEND_URL}/apidae/events?count=${COUNT}&first=${first}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setNumFound(d.numFound ?? 0); setEvents(d.items ?? []); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [first]);

  const totalPages = Math.ceil(numFound / COUNT) || 1;
  const currentPage = Math.floor(first / COUNT) + 1;

  return (
    <div className="cchmv-agenda">
      <div className="cchmv-agenda-header">
        <button className="cchmv-agenda-back" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}>‹</button>
        <div>
          <h1>Agenda du territoire</h1>
          <p className="cchmv-agenda-sub">Haute Maurienne Vanoise</p>
        </div>
        {!loading && !error && (
          <span className="cchmv-agenda-count">{numFound} évt.</span>
        )}
      </div>

      {loading && (
        <div className="cchmv-agenda-status">
          <span className="cchmv-agenda-status-icon">📅</span>
          <span>Chargement des événements…</span>
        </div>
      )}

      {!loading && error && (
        <div className="cchmv-agenda-status">
          <span className="cchmv-agenda-status-icon">⚠️</span>
          <span>Erreur : {error}</span>
        </div>
      )}

      {!loading && !error && (
        <div className="cchmv-agenda-list">
          {events.length === 0 && (
            <div className="cchmv-agenda-empty">
              <span>🗓</span>
              <span>Aucun événement pour cette période</span>
            </div>
          )}

          {events.map((evt) => {
            const periode = evt.ouverture?.periodesOuvertures?.[0];
            const dateStart = periode?.dateDebut;
            const dateEnd = periode?.dateFin;
            const image = evt.illustrations?.[0]?.traductionFichiers?.[0]?.urlFiche;
            const title = evt.nom?.libelleFr ?? 'Événement';
            const commune = evt.localisation?.adresse?.commune?.nom;
            const tag = getTag(evt);
            const dateLabel = formatDateRange(dateStart, dateEnd);

            return (
              <div key={evt.id} className="cchmv-agenda-event-wrap">
                <EventCard
                  image={image}
                  dateStart={dateStart}
                  dateEnd={dateEnd}
                  tag={tag}
                  title={title}
                  subtitle={commune}
                  style={{ height: 180 }}
                />
                {dateLabel && (
                  <p className="cchmv-agenda-event-dates">{dateLabel}</p>
                )}
                {evt.presentation?.descriptifCourt?.libelleFr && (
                  <p className="cchmv-agenda-event-desc">
                    {evt.presentation.descriptifCourt.libelleFr}
                  </p>
                )}
              </div>
            );
          })}

          <div className="cchmv-pagination">
            <button disabled={first === 0} onClick={() => setFirst(Math.max(0, first - COUNT))}>← Préc.</button>
            <span>{currentPage} / {totalPages}</span>
            <button disabled={first + COUNT >= numFound} onClick={() => setFirst(first + COUNT)}>Suiv. →</button>
          </div>
        </div>
      )}
    </div>
  );
}
